import memoizeLast from 'transmute/memoizeLast';
import compose from 'transmute/compose';
import styled from 'styled-components';
import { CALYPSO_LIGHT } from 'HubStyleTokens/colors';

// @ts-expect-error module not typed
import CalleeErrorBoundary from './callee-error/CalleeErrorBoundary';
import NoCalleesPrompt from './callee-numbers/components/NoCalleesPrompt';
import CallSettingsNumbersWorkspace from './callee-numbers/components/CallSettingsNumbersWorkspace';
// @ts-expect-error module not typed
import EnsureCallingAccess from './calling-appeals/components/EnsureCallingAccess';
import { getData, isSucceeded } from 'calling-lifecycle-internal/async-flux-utils/operators/getApiProperties';
import { getCallableObjects, getCallablePhoneNumberProperties, getHasAssociatedObjects, getPhoneNumberProperties } from 'calling-lifecycle-internal/callees/operators/calleesOperators';
import { getCallableObjectAndAssociations } from 'calling-lifecycle-internal/callee-omnibus/operators/calleeOmnibusResultGetters';

// @ts-expect-error module not typed
import withCalleeOmnibus from './callee-omnibus/hoc/withCalleeOmnibus';
import withCallingAccess from './calling-appeals/hoc/withCallingAccess';
import withWidgetState from './widget-state/hoc/withWidgetState';
// @ts-expect-error module not typed
import withAccessLevelPrefetch from './access-level/hoc/withAccessLevelPrefetch';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getCallingMinutesPerPortal } from 'calling-internal-common/utils/getCallingLimits';
import CallingOnboardingWorkspace from './callee-numbers/components/CallingOnboardingWorkspace';
import { isCallingFree } from 'calling-internal-common/utils/CallingSalesPro';
import { useHubSpotCallingUsage } from 'calling-lifecycle-internal/calls-v1-settings-usage/hooks/useHubSpotCallingUsage';
import { getHubspotProvidedNumbers, getInstalledCallingApplications, getPhoneNumbers, getHubspotSubAccountNumbers } from './callee-omnibus/operators/settingsOmnibusResultGetters';
import { useMemo } from 'react';
import { HELP_DESK } from 'calling-internal-common/constants/appIdentifiers';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const Wrapper = styled.div.withConfig({
  displayName: "CallWorkspacePopover__Wrapper",
  componentId: "sc-1t4trvr-0"
})(["max-width:615px;"]);
const Options = styled.div.withConfig({
  displayName: "CallWorkspacePopover__Options",
  componentId: "sc-1t4trvr-1"
})([".option-item:hover{background-color:", ";}"], CALYPSO_LIGHT);
const Container = styled.div.withConfig({
  displayName: "CallWorkspacePopover__Container",
  componentId: "sc-1t4trvr-2"
})(["padding:10px;max-width:300px;"]);
const CallWorkspacePopover = props => {
  const hasCallingMinutes = !!getCallingMinutesPerPortal(props.limits);
  const userHasPaidSeat = !isCallingFree(props.scopes);
  const {
    minutesUsage
  } = useHubSpotCallingUsage({
    hasCallingMinutes,
    userHasPaidSeat
  });
  const {
    asyncSettingsOmnibus,
    inboxId,
    appIdentifier
  } = props;
  const data = getData(asyncSettingsOmnibus);
  const fromNumbers = getPhoneNumbers(data);
  const hubspotSubAccountNumbers = getHubspotSubAccountNumbers(fromNumbers);
  const hubspotProvidedNumbers = getHubspotProvidedNumbers(fromNumbers);
  const installedCallingApplications = getInstalledCallingApplications(data);
  const showChangeProviderLink = useMemo(() => installedCallingApplications && installedCallingApplications.length > 0, [installedCallingApplications]);
  const isHelpdesk = appIdentifier === HELP_DESK;
  const channelAssignedPhoneNumbers = isHelpdesk ? hubspotProvidedNumbers.filter(hsNumber => {
    const workspaceObject = hsNumber.get('workspaceIdAndType');
    return !!workspaceObject && workspaceObject.workspaceId === inboxId;
  }) : hubspotProvidedNumbers.concat(hubspotSubAccountNumbers);
  const hasCallableRecords = memoizeLast(calleesData => {
    const hasAssociatedObjects = getHasAssociatedObjects(calleesData);
    if (hasAssociatedObjects) {
      return true;
    }
    const callableObjects = getCallableObjects(calleesData);
    return callableObjects && callableObjects.some(callableObject => {
      const phoneNumberProperties = getPhoneNumberProperties(callableObject);
      return getCallablePhoneNumberProperties(phoneNumberProperties).size > 0;
    });
  });
  const renderNoCalleesMessage = () => {
    const {
      objectTypeId,
      subjectId
    } = props;
    return /*#__PURE__*/_jsx(NoCalleesPrompt, {
      subjectId: Number(subjectId),
      objectTypeId: objectTypeId,
      shouldShowAddNumber: false
    });
  };
  const renderNoFromNumberAccess = () => {
    return /*#__PURE__*/_jsx(Container, {
      children: /*#__PURE__*/_jsx("strong", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "callee-selection.noAccessToFromNumber"
        })
      })
    });
  };
  const renderCallees = () => {
    const {
      onCallButtonClick,
      asyncCalleeOmnibus,
      widgetState,
      selectedCallProvider,
      selectedCallMethod,
      scopes,
      defaultFromNumber,
      isKnownVisitor,
      visitorPhoneNumber,
      gates
    } = props;
    const calleeOmnibusData = getData(asyncCalleeOmnibus);
    const calleesData = calleeOmnibusData && getCallableObjectAndAssociations(calleeOmnibusData);
    const hasRecords = hasCallableRecords(calleesData);
    return /*#__PURE__*/_jsx(_Fragment, {
      children: !isSucceeded(asyncCalleeOmnibus) || hasRecords || visitorPhoneNumber ? /*#__PURE__*/_jsx(Options, {
        className: "flex-column",
        "data-selenium-test": "call-settings-numbers-options",
        children: /*#__PURE__*/_jsx(CallSettingsNumbersWorkspace, {
          appIdentifier: appIdentifier,
          asyncCalleeOmnibus: asyncCalleeOmnibus,
          minutesUsage: minutesUsage,
          onCallButtonClick: onCallButtonClick,
          scopes: scopes,
          selectedCallMethod: selectedCallMethod,
          selectedCallProvider: selectedCallProvider,
          defaultFromNumber: defaultFromNumber,
          visitorPhoneNumber: visitorPhoneNumber,
          isKnownVisitor: isKnownVisitor,
          widgetState: widgetState,
          channelAssignedPhoneNumbers: channelAssignedPhoneNumbers,
          showChangeProviderLink: showChangeProviderLink,
          gates: gates
        })
      }) : renderNoCalleesMessage()
    });
  };
  const {
    callingAccess,
    limits,
    scopes,
    gates
  } = props;
  return /*#__PURE__*/_jsx(Wrapper, {
    children: /*#__PURE__*/_jsx(CalleeErrorBoundary, {
      appName: "CALLING",
      children: /*#__PURE__*/_jsx(EnsureCallingAccess, {
        appIdentifier: appIdentifier,
        useFullWidth: false,
        scopes: scopes,
        callingAccess: callingAccess,
        children: /*#__PURE__*/_jsx(CallingOnboardingWorkspace, {
          appIdentifier: appIdentifier,
          asyncSettingsOmnibus: asyncSettingsOmnibus,
          gates: gates,
          limits: limits,
          scopes: scopes,
          children: channelAssignedPhoneNumbers.size > 0 ? renderCallees() : renderNoFromNumberAccess()
        })
      })
    })
  });
};
export default compose(withCalleeOmnibus, withCallingAccess, withWidgetState, withAccessLevelPrefetch)(CallWorkspacePopover);